import { useCallback, useState } from 'react';
import { getAccessToken, clearSession } from '../services/tokenManager';
import { useAuthStore } from '../stores/authStore';
import { UserWithTenant } from '../types/models';

/**
 * Hook d'authentification
 * ✅ Utilise UserWithTenant pour la structure complète User + contexte tenant
 * ✅ Aligné avec backend Prisma V2 (octobre 2025)
 */
interface AuthState {
  user: UserWithTenant | null;
  isAuthenticated: boolean;
}

export const useAuth = () => {
  const [authState, setAuthState] = useState<AuthState>(() => {
    const token = getAccessToken();
    const user = useAuthStore.getState().user;

    if (token && user) {
      return { user, isAuthenticated: true };
    }

    return { user: null, isAuthenticated: false };
  });

  const login = useCallback((userData: UserWithTenant, token: string) => {
    // Note: This should use sessionStorage.setSession() instead
    // Kept for backward compatibility but may not work correctly with session refresh
    setAuthState({ user: userData, isAuthenticated: true });
  }, []);

  const logout = useCallback(() => {
    clearSession();
    setAuthState({ user: null, isAuthenticated: false });
  }, []);

  return {
    user: authState.user,
    isAuthenticated: authState.isAuthenticated,
    login,
    logout
  };
};
